import { Component, OnChanges, Input, SimpleChanges, inject } from '@angular/core';
import { formatDate, NgClass, DecimalPipe, PercentPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { ExcelService, rowType, styleType } from "app/services/excel.service";
import { assetType, PortfolioTransactionsComponent } from './portfolio-transactions.component';
import { AppConfig } from "app/app.config";

import { APIService } from 'app/services/api.service';
import { AuthenticationService } from 'app/services/auth.service';
import { LoaderService } from "app/services/loader.service";
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { MinusSignToParens } from 'app/pipes/minus-sign-to-parens.pipe';
import { PlaceholderPipe } from 'app/pipes/placeholder.pipe';

type apiDataType = {
  AcctInception: string;
  AssetSubClass: string;
  CadInceptionReturn: number;
  CADMarketValue: number;
  CadYtdReturn: number;
  ClosedDate: string;
  Currency: string;
  CurrentPrice: number;
  CusipNum: string;
  HoldingId: number;
  InceptionReturn: number;
  IsActive: number;
  MarketValue: number;
  ProductName: string;
  TotalUnits: number;
  USMarketValue: number;
  YtdReturn: number;
};

type assetsType = {
  Inactive: assetType[];
  CAD: assetType[];
  USD: assetType[];
};

type assetGroup = 'CAD' | 'USD' | 'Inactive';

type planType = {
  id: string;
  Currency?: string;
  PlanName?: string;
  title?: string;
  TargetNameID1?: number;
  TargetNameID2?: number;
};

@Component({
    selector: 'portfolio-assets',
    templateUrl: 'portfolio-assets.component.html',
    styleUrls: ['portfolio-assets.component.scss'],
    imports: [NgClass, NgbTooltip, PortfolioTransactionsComponent, MinusSignToParens, DecimalPipe, PercentPipe, CurrencyPipe, DatePipe, PlaceholderPipe]
})

export class PortfolioAssetsComponent implements OnChanges {
  private api = inject(APIService);
  private auth = inject(AuthenticationService);
  private loaderService = inject(LoaderService);
  private config = inject(AppConfig);
  private excel = inject(ExcelService);

  @Input() plan?: planType;
  @Input() YTDReturn = 0;
  @Input() InceptionReturn = 0;
  @Input() isPortfolioInvestmentPage = false;
  @Input() CadYTDReturn = 0;
  @Input() CadInceptionReturn = 0;
  @Input() isPershing = false;

  public groups: assetGroup[] = ['CAD', 'USD', 'Inactive'];

  public assets: assetsType = {
    Inactive: [],
    CAD: [],
    USD:[],
  };
  public holding?: assetType;
  public valuationDate = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.prototype.hasOwnProperty.call(changes,'plan') && (changes['plan'].previousValue == undefined || changes['plan'].currentValue.id != changes['plan'].previousValue.id)) {
      this.holding = undefined;

      this.loaderService.display(true, 'PortfolioAssets');
      const reRg = this.auth.getCurrentReRg()!;
      this.valuationDate = this.auth.getCurrentValuationDate();
      const planPath = this.api.getPlanPath(this.plan?.id || '', reRg.IsPershing);

      this.api.get<apiDataType[]>(`${this.config.portfolio.portfolioAssets}/${reRg.ReRgId}/${this.valuationDate}/${planPath}`).subscribe(
        data => {
          this.assets = { Inactive:[], CAD:[], USD:[]};
          data.forEach(a => {
            const processedAsset: assetType = {
              AcctInception: a.AcctInception,
              AssetSubClass: a.AssetSubClass,
              ClosedDate: a.ClosedDate,
              CurrentPrice: a.CurrentPrice,
              CusipNum: a.CusipNum,
              CusipNumFormated: this.formatCusipNum(a.CusipNum),
              HoldingId: a.HoldingId,
              InceptionReturn: a.InceptionReturn,
              MarketValue: a.MarketValue,
              ProductName: a.ProductName,
              TotalUnits: a.TotalUnits,
              YtdReturn: a.YtdReturn,
              CADMarketValue: a.CADMarketValue,
            };

            if (a.Currency == 'CAD') {
              processedAsset.MarketValue = a.CADMarketValue;
              processedAsset.YtdReturn = a.CadYtdReturn;
              processedAsset.InceptionReturn = a.CadInceptionReturn;
              if (a.IsActive == 0) this.assets.Inactive.push(processedAsset);
              else this.assets.CAD.push(processedAsset);
            }
            else {
              processedAsset.MarketValue = a.USMarketValue;
              if (a.IsActive == 0) this.assets.Inactive.push(processedAsset);
              else this.assets.USD.push(processedAsset);
            }
          }, this);
          this.loaderService.display(false, 'PortfolioAssets');
        },
        () => {
          this.loaderService.display(false, 'PortfolioAssets');
        }
      );
    }
    if (window['isMobileSafari']) { // defined in app.component
      const tm = $('#transactionsModal');
      let tm_parent: JQuery<HTMLElement>;
      let pos = 0;
      tm.on('show.bs.modal', () => {
        pos = $(window).scrollTop() || 0;
        tm_parent = tm.parent();
        tm.appendTo('body');
        $('body').addClass('transactionsModal-MobileSafari');
      });
      tm.on('hide.bs.modal', () => {
        $('body').removeClass('transactionsModal-MobileSafari');
        $(window).scrollTop(pos);
      });
      tm.on('hidden.bs.modal', () => {
        tm.appendTo(tm_parent);
      });
    }
  }

  formatCusipNum(cusip: string) {
    return (cusip === '' || cusip === undefined || cusip.startsWith('CASH')) ? '' : ' (' + cusip.trim() + ')';
  }

  isLessThanOneYear(acctInceptionDate: string, percentReturn: number): boolean {
    const d1 = this.valuationDate!.toNWMdate();
    const d2 = acctInceptionDate.toNWMdate();
    const diff = (( +d1 - +d2 ) / 1000/60/60/24);
    return diff < 365 && percentReturn === 0;
  }

  onHoldingClick(holding: assetType) {
    this.holding = undefined;
    setTimeout(() => {this.holding = holding});
  }

  sumMarketValues(assets: assetType[]) {
    return assets.reduce((marketValue, asset) => marketValue + asset.MarketValue, 0);
  }
  sumCADMarketValues(assets: assetType[]) {
    return assets.reduce((marketValue, asset) => marketValue + asset.CADMarketValue, 0);
  }

  export() {
    const type = this.assets['CAD'].length > 0 ? 'CAD' : 'USD';
    if (this.assets[type].length == 0) return;
    const headers = [
        "Investment ("+type+")","Current\r\nPrice ($)","Current Market\r\nValue ($)","YTD Return",
        "Annualized Return\r\nSince Inception *","Performance\r\nStart Date"
    ];

    const lastCol = headers.length-1;

    const rows: rowType[] = [
      [this.plan?.title, '', '', '', '', this.excel.toExcelLocalTime(new Date()) ],
      [type + ' Investment Summary as of ' + formatDate(this.valuationDate!, 'MMM d, yyyy', 'en-US')],
      [],
      headers,
    ];
    const styles: styleType[] = [
      // styles can be applied to row, col or cell, but if you do it more then once, only the latest will apply
      {col: [0], font: 'Arial 10 #000000 B', align: 'L C', width: 20}, // make first column bold
      {col: '1-' + lastCol, align: 'R C', width: 20}, // right-center align numeric columns
      {row: 0, font: '14'},
      {row: 1, font: '12'},
      {row: 3, height: 30},
      {row: 0, col: 5, format: 'm-d-yyyy h:mm:ss AM/PM', font: '8'},
      {row: 3, col: '0-' + lastCol, fill: '#183750', font: 'Arial 10 #ffffff B', wrapText: 1},
    ];
    // let sumOriginalCost = 0;
    let sumMarketValue = 0;

    this.assets[type].forEach(asset => {
      // sumOriginalCost+= asset.OriginalCost;
      sumMarketValue += asset.MarketValue;
      rows.push([
        asset.ProductName + this.formatCusipNum(asset.CusipNum),
        asset.CurrentPrice, // this.formatNumber(asset.OriginalCost),
        asset.MarketValue, // this.formatNumber(asset.MarketValue),
        asset.YtdReturn, // this.formatPercent(asset.YtdReturn),
        this.isLessThanOneYear(asset.AcctInception, asset.InceptionReturn) ? 'N/A' : asset.InceptionReturn, // this.formatPercent(asset.InceptionReturn)
        formatDate(asset.AcctInception, 'MMM d, yyyy', 'en-US')
      ]);

      styles.push({row: rows.length - 1, col: '1-2', format: '#,##0.00;(#,##0.00);&quot;-&quot;'});
      styles.push({row: rows.length - 1, col: 3, format: '0.00%;(0.00%);&quot;-&quot;'});
      if (!this.isLessThanOneYear(asset.AcctInception, asset.InceptionReturn)) {
        styles.push({row: rows.length - 1, col: 4, format: '0.00%;(0.00%);&quot;-&quot;'});
      }

    });
    rows.push([
      'TOTAL',
      '',
      sumMarketValue,
      this.YTDReturn,
      this.InceptionReturn,
      ''
    ]);
    styles.push({row: rows.length - 1, col: [0, lastCol], fill: '#8799ab', font: 'Arial 10 #ffffff B', border: 'none,none,none,thin #ffffff'});

    styles.push({row: rows.length - 1, col: '1-2', format: '$#,##0.00;(#,##0.00);&quot;-&quot;', fill: '#8799ab', font: 'Arial 10 #ffffff B', border: 'none,none,none,thin #ffffff'});
    styles.push({row: rows.length - 1, col: '3-4', format: '0.00%;(0.00%);&quot;-&quot;', fill: '#8799ab', font: 'Arial 10 #ffffff B', border: 'none,none,none,thin #ffffff'});

    rows.push([], [],
      ['E. & O.E. for illustration purposes only. Past performance is not necessarily indicative of future performance. Please refer to the FAQ section and your Nicola Wealth Portfolio Statements located in the Documents section of your Nicola Wealth Private Client Access Website for additional disclosures.'],
      ['* If the inception date of your account is less than one (1) year, the "Annualized Return Since Inception" displayed is calculated as a cumulative return from inception.']);
    styles.push({row: [rows.length - 2, rows.length - 1], font: '8'});

    this.excel.generate('AccountInvestmentSummary.xlsx', [{sheetTitle: 'Account Investment Summary', rows: rows, styles: styles}]);
  }
}
